/**
 * Onboarding API Utilities
 *
 * Handles communication with the Raise AI API for onboarding form data
 */

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'https://raiseai-api-production.up.railway.app';

/**
 * Get location ID from URL parameters or localStorage
 */
export function getLocationId() {
  // Check URL parameters first
  const params = new URLSearchParams(window.location.search);
  const locationIdFromUrl = params.get('locationId') || params.get('location_id');

  if (locationIdFromUrl) {
    // Store for future use
    localStorage.setItem('locationId', locationIdFromUrl);
    return locationIdFromUrl;
  }

  // Fall back to stored locationId
  return localStorage.getItem('locationId') || null;
}

/**
 * Get user email from URL parameters or localStorage
 */
export function getUserEmail() {
  // Check URL parameters first
  const params = new URLSearchParams(window.location.search);
  const emailFromUrl = params.get('email');

  if (emailFromUrl) {
    // Store for future use
    localStorage.setItem('userEmail', emailFromUrl);
    return emailFromUrl;
  }

  // Fall back to stored email
  return localStorage.getItem('userEmail') || null;
}

/**
 * Save form progress to API
 * @param {string} step - The form step name (e.g., 'deal-info', 'documents', etc.)
 * @param {object} data - The form data for this step
 * @returns {Promise<object>} API response
 */
export async function saveProgress(step, data) {
  const locationId = getLocationId();
  const email = getUserEmail();

  if (!locationId && !email) {
    throw new Error('Location ID or email required. Please ensure parameters are in URL.');
  }

  try {
    const response = await fetch(`${API_BASE_URL}/api/onboarding/save-progress`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        locationId,  // Preferred lookup value
        email,       // Fallback for backward compatibility
        step,
        data
      })
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to save progress');
    }

    return await response.json();
  } catch (error) {
    console.error('Error saving progress:', error);
    throw error;
  }
}

/**
 * Mark onboarding as complete
 * @returns {Promise<object>} API response
 */
export async function completeOnboarding() {
  const locationId = getLocationId();
  const email = getUserEmail();

  if (!locationId && !email) {
    throw new Error('Location ID or email required. Please ensure parameters are in URL.');
  }

  try {
    const response = await fetch(`${API_BASE_URL}/api/onboarding/complete`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ locationId, email })
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to complete onboarding');
    }

    return await response.json();
  } catch (error) {
    console.error('Error completing onboarding:', error);
    throw error;
  }
}

/**
 * Get onboarding status
 * @returns {Promise<object>} Status object with completed flag and form data
 */
export async function getOnboardingStatus() {
  const locationId = getLocationId();
  const email = getUserEmail();

  if (!locationId && !email) {
    throw new Error('Location ID or email required. Please ensure parameters are in URL.');
  }

  try {
    // Build query string with available parameters
    const params = new URLSearchParams();
    if (locationId) params.append('locationId', locationId);
    if (email) params.append('email', email);

    const response = await fetch(`${API_BASE_URL}/api/onboarding/status?${params.toString()}`);

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to get onboarding status');
    }

    return await response.json();
  } catch (error) {
    console.error('Error getting onboarding status:', error);
    throw error;
  }
}

/**
 * Load saved form data from API
 * @param {string} step - The form step name to load data for
 * @returns {Promise<object>} Form data for the specified step
 */
export async function loadFormData(step) {
  try {
    const status = await getOnboardingStatus();

    if (status.formData && status.formData[step]) {
      return status.formData[step];
    }

    return {};
  } catch (error) {
    console.error('Error loading form data:', error);
    return {};
  }
}

/**
 * Hook-style wrapper for auto-saving form data
 * Returns save function and loading state
 */
export function useAutoSave(step) {
  const saveWithDebounce = (data) => {
    // Clear any existing timeout
    if (window._autoSaveTimeout) {
      clearTimeout(window._autoSaveTimeout);
    }

    // Set new timeout
    window._autoSaveTimeout = setTimeout(() => {
      saveProgress(step, data)
        .then(() => {
          console.log(`Auto-saved ${step} data`);
        })
        .catch(error => {
          console.error(`Failed to auto-save ${step}:`, error);
          // Fall back to localStorage on error
          localStorage.setItem(`formData_${step}`, JSON.stringify(data));
        });
    }, 1000); // 1 second debounce
  };

  return { saveWithDebounce };
}
